import React, { useState, useRef, useCallback, useId } from "react";
import { PMDialog } from "../../utils/PMDialog";
import { PMField } from "../../utils/PMField";
import { CameraIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { UploaderV4 } from "./Uploaderv4";
import { TWclassNames } from "../../utils/Div";
import PdfPlaceholder from "../../../utils/pdf-placeholder.png";

export const DocumentUploadSectionV4 = (props) => {
  const {
    multiple,
    module,
    moduleParam,
    module_param,
    parentCallback,
    required,
    disabled,
    iconOnly,
    compact,
  } = props;
  const resolvedModuleParam = moduleParam ?? module_param;
  const [open, setOpen] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedCount, setUploadedCount] = useState(0);
  const [startUpload, setStartUpload] = useState(null);
  const fileInputRef = useRef(null);
  const [resetUploader, setResetUploader] = useState(null); // ✅ Store reset function
  const fileInputId = useId();

  const defaultTitleFromFile = (file) => {
    if (!file?.name) return "";
    const base = file.name.replace(/\.[^.]+$/i, "");
    const cleaned = base.replace(/[_-]+/g, " ").trim();
    return cleaned || file.name;
  };

  const allowedFileTypes =
    props.allowedFileTypes ||
    "image/png, image/jpeg, image/jpg, image/gif, image/svg, image/svg+xml, image/webp, application/pdf";

  const handleFileSelection = (event) => {
    const selectedFiles = Array.from(event.target.files);
    if (!selectedFiles.length) return;

    const staged = selectedFiles.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      title: defaultTitleFromFile(file),
      module,
      moduleParam: resolvedModuleParam,
    }));

    setFilesToUpload((prev) => [...prev, ...staged]);
    // Defer opening until native file picker is fully closed (avoids focus / accidental triggers on some browsers).
    requestAnimationFrame(() => setOpen(true));
    // Allow picking the same file again and multi-step "Legg til flere"
    event.target.value = "";
  };

  const handleTitleChange = (index, title) => {
    setFilesToUpload((prev) => {
      const updated = [...prev];
      updated[index].title = title;
      return updated;
    });
  };

  const handleUploadComplete = (uploadedFiles) => {
    console.log("Upload Complete:", uploadedFiles);
    setFilesToUpload([]);
    setIsUploading(false);
    if (parentCallback) {
      parentCallback(uploadedFiles);
    }
    handleClose();
  };

  const handleProgressChange = (progress) => {
    setUploadProgress((prev) => {
      const newProgress = { ...prev, ...progress };
      const totalProgress = Object.values(newProgress).reduce(
        (acc, val) => acc + val,
        0
      );

      return newProgress;
    });
  };

  const handleAddMoreFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileUploadComplete = () => {
    setUploadedCount((prev) => prev + 1);
  };

  const handleClose = () => {
    setOpen(false);
    setFilesToUpload([]);
    setIsUploading(false);
    setUploadedCount(0);
    setUploadProgress({});

    if (resetUploader) {
      resetUploader(); // ✅ Call reset function from UploaderV4
    }
  };

  const handleUploadStart = useCallback((uploadFn) => {
    if (uploadFn && typeof uploadFn === "function") {
      setStartUpload(uploadFn);
    } else {
      console.error("Invalid upload function received:", uploadFn);
    }
  }, []);

  return (
    <>
      {/* Hidden File Input */}
      <input
        id={fileInputId}
        accept={allowedFileTypes}
        type="file"
        multiple={multiple}
        ref={fileInputRef}
        className="sr-only"
        onChange={handleFileSelection}
      />

      {/* File Picker Button */}
      <div
        className={TWclassNames(
          required ? "border-2 border-red-400" : "",
          compact
            ? "rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors cursor-pointer"
            : "text-center bg-blue-400 hover:bg-blue-600 border-gray-300 px-5 py-5"
        )}
      >
        <div
          className={TWclassNames(
            disabled ? "cursor-not-allowed" : "cursor-pointer",
            compact ? "flex items-center justify-center" : "bg-white relative rounded-md font-medium text-white focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-400 focus-within:ring-offset-2"
          )}
        >
          <button
            type="button"
            onClick={() => !disabled && fileInputRef.current?.click()}
            className={TWclassNames(
              "flex flex-row items-center justify-center",
              compact && "p-1"
            )}
          >
            <CameraIcon className={TWclassNames("h-5 w-6", compact ? "text-gray-500" : "mr-2 text-white")} />
            {!iconOnly && (
              <div className="flex-row">
                <span>Last opp dokument/ bilde</span>
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Upload Dialog */}
      <PMDialog
        open={open}
        onClose={handleClose}
        title="Forhåndsvisning og opplasting"
      >
        {filesToUpload.length > 0 && (
          <>
            {filesToUpload.map((file, index) => (
              <div
                key={index}
                className="flex flex-col gap-3 py-4 bg-gray-100 rounded-xl px-4"
              >
                <img
                  src={
                    file.file.type === "application/pdf"
                      ? PdfPlaceholder
                      : file.previewUrl
                  }
                  className="h-40 object-cover rounded"
                  alt="Preview"
                />
                <PMField
                  name="title"
                  type="text"
                  label="Tittel"
                  value={file.title}
                  onChange={(e) => handleTitleChange(index, e.target.value)}
                />
              </div>
            ))}

            {/* Progress Bar */}
            {isUploading && filesToUpload.length > 0 && (
              <div className="mt-4 w-full bg-gray-300 rounded h-4">
                <div
                  className="h-full bg-blue-500 rounded"
                  style={{
                    width: `${
                      (Object.values(uploadProgress).reduce(
                        (acc, val) => acc + val,
                        0
                      ) /
                        (filesToUpload.length * 100)) *
                      100
                    }%`,
                  }}
                />
              </div>
            )}
            {/* Upload Status */}
            {isUploading && (
              <p className="text-center mt-2 text-sm text-gray-700">
                Opplasting {uploadedCount + 1} av {filesToUpload.length}
              </p>
            )}

            {/* Upload Complete Checkmark */}
            {uploadedCount === filesToUpload.length && isUploading && (
              <div className="flex justify-center items-center mt-4 text-green-500">
                <CheckCircleIcon className="h-6 w-6" />
                <span className="ml-2 text-green-600 font-semibold">
                  Opplasting fullført
                </span>
              </div>
            )}

            {/* Add More Files Button */}
            <button
              type="button"
              className="mt-4 flex items-center justify-center w-full bg-blue-500 text-white p-2 rounded"
              onClick={handleAddMoreFiles}
            >
              <CameraIcon className="h-5 w-5 mr-2" /> Legg til flere bilder
            </button>

            {/* Upload Button */}
            <div className="flex justify-between mt-4">
              <button
                type="button"
                className="w-20 bg-gray-600 text-white p-2 rounded"
                onClick={() => handleClose()}
              >
                Steng
              </button>

              <button
                type="button"
                disabled={isUploading || !startUpload}
                className="w-20 p-2 rounded text-white bg-emerald-600 hover:bg-blue-500 disabled:bg-gray-400"
                onClick={() => {
                  if (!isUploading && typeof startUpload === "function") {
                    setIsUploading(true);
                    setUploadedCount(0);
                    startUpload().then(() => setIsUploading(false));
                  } else {
                    console.error(
                      "startUpload is not a function or is undefined:",
                      startUpload
                    );
                  }
                }}
              >
                {isUploading ? "Laster opp..." : "Last opp"}
              </button>
            </div>
          </>
        )}
      </PMDialog>

      {/* Upload Handler */}
      <UploaderV4
        filesToUpload={filesToUpload}
        onUploadStart={handleUploadStart}
        onUploadComplete={handleUploadComplete}
        onProgressChange={handleProgressChange}
        onFileUploadComplete={handleFileUploadComplete} // ✅ NEW
        onReset={setResetUploader} // ✅ Pass reset function
      />
    </>
  );
};
